import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  FormControl,
  TextField,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { phoneFormatInput } from "../../../../../../utils/Formatter";
import DialogContainer from "../../../../../../components/dialog-container/dialog-container";
import AlternativePhoneField from "../../../../../../components/alternative-phone/alternative-phone";
import TextInput from "../../../../../../components/textinput/textinput";
import AdditionalContactField from "../../../../../../components/additional-contact/additional-contact";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import { updateInspectionFirmContactsRequest } from "../../../../../../utils/APICalls";
import Loading from "../../../../../../components/loading/loading";
import ErrorNotification from "../../../../../../components/error-notification/error-notification";
import {
  getInspectionFirmAllUsersRequest,
  getInspectionFirmDetailsRequest,
} from "../../../../../../redux/inspection-firms/slice";
import {
  getCountriesListRequest,
  getStatesByCountryRequest,
} from "../../../../../../redux/locations/slice";

type props = {
  showDialog: boolean;
  closeDialog: any;
  data?: any;
};

interface FormValues {
  mailingCountry?: string;
  mailingStreetAddress?: string;
  mailingAddress2?: string;
  mailingCity?: string;
  mailingState?: string;
  mailingZip?: string;
  billingCountry?: string;
  billingStreetAddress?: string;
  billingAddress2?: string;
  billingCity?: string;
  billingState?: string;
  billingZip?: string;
}

const ValidationSchema = yup.object({
  mailingCountry: yup.string().required("This field can't be empty"),
  mailingStreetAddress: yup.string().required("This field can't be empty"),
  mailingCity: yup.string().required("This field can't be empty"),
  mailingState: yup.string().required("This field can't be empty"),
  mailingZip: yup
    .string()
    .required("This field can't be empty")
    .min(5, "Incorrect Zip")
    .max(7, "Incorrect Zip"),
  billingCountry: yup.string().required("This field can't be empty"),
  billingStreetAddress: yup.string().required("This field can't be empty"),
  billingCity: yup.string().required("This field can't be empty"),
  billingState: yup.string().required("This field can't be empty"),
  billingZip: yup
    .string()
    .required("This field can't be empty")
    .min(5, "Incorrect Zip")
    .max(7, "Incorrect Zip"),
});

export default function EditContactDetails({
  showDialog,
  closeDialog,
  data,
}: props) {
  const [selectedPhone, setSelectedPhone] = useState("altPhone0");
  const [selectedManager, setSelectedManager] = useState("contact0");
  const [selectedContact, setSelectedContact] = useState("contact0");
  const [phones, setPhones] = useState<any[]>([]);
  const [accountManagers, setAccountManagers] = useState<any[]>([]);
  const [isAccountManagerEmptyError, setIsAccountManagerEmptyError] =
    useState(false);
  const [accountingContacts, setAccountingContacts] = useState<any[]>([]);
  const [isAccountingContactEmptyError, setIsAccountingContactEmptyError] =
    useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [phoneFieldErrors, setPhoneFieldErrors] = useState<string[]>([]);
  const [mailingStates, setMailingStates] = useState<string[]>([]);
  const [billingStates, setBillingStates] = useState<string[]>([]);

  const dispatch = useDispatch();
  const { loading } = useSelector((state: any) => state.inspectionFirms);
  const { inspectionFirmAllUsers } = useSelector(
    (state: any) => state.inspectionFirms
  );
  const { countries, states } = useSelector((state: any) => state.location);

  useEffect(() => {
    if (data) {
      dispatch(getInspectionFirmAllUsersRequest({ id: data.id }));
      dispatch(getCountriesListRequest());
      // Phones
      const allPhones = [];
      const primaryList = data?.phones?.filter((e: any) => e.isPrimary);
      if (primaryList.length > 0) {
        allPhones.push(primaryList[0]);
      }
      const alternativeList = data?.phones?.filter(
        (e: any) => e.isPrimary === false
      );
      if (alternativeList.length > 0) {
        allPhones.push(...alternativeList);
      }
      if (allPhones.length === 0) {
        allPhones.push({ phone: "", isPrimary: true });
      }
      setPhones(allPhones);

      // Account Managers
      const allAccountManagers = [];
      const primaryAccountManager = data?.accountManagers?.filter(
        (e: any) => e.isPrimary
      );
      if (primaryAccountManager.length > 0) {
        allAccountManagers.push(primaryAccountManager[0]);
      }
      const nonPrimaryAccountManager = data?.accountManagers?.filter(
        (e: any) => e.isPrimary === false
      );
      if (nonPrimaryAccountManager.length > 0) {
        allAccountManagers.push(...nonPrimaryAccountManager);
      }
      if (allAccountManagers.length === 0) {
        allAccountManagers.push({ userId: "", isPrimary: true });
      }
      setAccountManagers(allAccountManagers);

      // Accounting Contacts
      const allAccountingContacts = [];
      const primaryAccountingContacts = data?.accountingContacts?.filter(
        (e: any) => e.isPrimary
      );
      if (primaryAccountingContacts.length > 0) {
        allAccountingContacts.push(primaryAccountingContacts[0]);
      }
      const nonPrimaryAccountingContacts = data?.accountingContacts?.filter(
        (e: any) => e.isPrimary === false
      );
      if (nonPrimaryAccountingContacts.length > 0) {
        allAccountingContacts.push(...nonPrimaryAccountingContacts);
      }
      if (allAccountingContacts.length === 0) {
        allAccountingContacts.push({ userId: "", isPrimary: true });
      }
      setAccountingContacts(allAccountingContacts);
    }
  }, [data]); // eslint-disable-line

  const normalizePhone = (phone: string) => phone.replace(/\D/g, "");

  const validatePhones = () => {
    let hasError = false;
    const phoneErrors: string[] = [];
    const normalizedToIndex: Record<string, number> = {};
    phones.forEach((phone, idx) => {
      if (!phone.phone) {
        phoneErrors[idx] = "";
      } else {
        phoneErrors[idx] = "";
        const normalized = normalizePhone(phone.phone);
        if (normalized && normalizedToIndex.hasOwnProperty(normalized)) {
          phoneErrors[idx] = "Duplicate phone not allowed.";
          hasError = true;
        } else {
          normalizedToIndex[normalized] = idx;
        }
      }
    });
    if (formik.submitCount > 0) {
      phones.forEach((phone, idx) => {
        if (!phone.phone) {
          phoneErrors[idx] = "This field can't be empty";
          hasError = true;
        }
      });
    }
    const primaryCount = phones.filter((phone) => phone.isPrimary).length;
    if (primaryCount !== 1) {
      phones.forEach((_, idx) => {
        phoneErrors[idx] =
          primaryCount === 0
            ? "Please select a primary phone number."
            : primaryCount > 1 && phones[idx].isPrimary
            ? "Only one phone can be primary."
            : phoneErrors[idx];
        hasError = true;
      });
    }
    setPhoneFieldErrors(phoneErrors);
    return hasError;
  };

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      mailingCountry: data?.mailingAddress?.country || "",
      mailingStreetAddress: data?.mailingAddress?.street || "",
      mailingAddress2: data?.mailingAddress?.address2 || "",
      mailingCity: data?.mailingAddress?.city || "",
      mailingState: data?.mailingAddress?.state || "",
      mailingZip: data?.mailingAddress?.zip || "",
      billingCountry: data?.billingAddress?.country || "",
      billingStreetAddress: data?.billingAddress?.street || "",
      billingAddress2: data?.billingAddress?.address2 || "",
      billingCity: data?.billingAddress?.city || "",
      billingState: data?.billingAddress?.state || "",
      billingZip: data?.billingAddress?.zip || "",
    },
    validationSchema: ValidationSchema,
    onSubmit: (value) => {
      setPhoneFieldErrors([]);
      if (validatePhones()) {
        return;
      }
      if (isPhoneDuplicate()) {
        return;
      }
      if (isAccountManagerEmpty()) {
        setIsAccountManagerEmptyError(true);
        return;
      }
      if (isAccountManagerDuplicate()) {
        return;
      }
      if (isAccountingContactEmpty()) {
        setIsAccountingContactEmptyError(true);
        return;
      }
      if (isAccountingContactDuplicate()) {
        return;
      }
      const managers: any[] = [];
      accountManagers.forEach((accountManager) => {
        const manager = {
          userId: accountManager.userId,
          isPrimary: accountManager.isPrimary,
        };
        managers.push(manager);
      });
      const contacts: any[] = [];
      accountingContacts.forEach((accountingContact) => {
        const contact = {
          userId: accountingContact.userId,
          isPrimary: accountingContact.isPrimary,
        };
        contacts.push(contact);
      });
      const payload = {
        id: data.id,
        phones: phones,
        mailingAddress: {
          street: value.mailingStreetAddress,
          address2: value.mailingAddress2,
          city: value.mailingCity,
          state: value.mailingState,
          country: value.mailingCountry,
          zip: value.mailingZip,
        },
        billingAddress: {
          street: value.billingStreetAddress,
          address2: value.billingAddress2,
          city: value.billingCity,
          state: value.billingState,
          country: value.billingCountry,
          zip: value.billingZip,
        },
        accountManagers: managers,
        accountingContacts: contacts,
      };
      updateInspectionFirmContacts(payload);
    },
  });

  useEffect(() => {
    if (formik.values.mailingCountry) {
      dispatch(
        getStatesByCountryRequest({ country: formik.values.mailingCountry })
      );
    }
  }, [formik.values.mailingCountry]); //eslint-disable-line

  useEffect(() => {
    if (formik.values.billingCountry) {
      dispatch(
        getStatesByCountryRequest({ country: formik.values.billingCountry })
      );
    }
  }, [formik.values.billingCountry]); //eslint-disable-line

  useEffect(() => {
    if (states && states?.country === formik.values.mailingCountry) {
      setMailingStates(states?.states);
    }
    if (states && states?.country === formik.values.billingCountry) {
      setBillingStates(states?.states);
    }
  }, [states]); //eslint-disable-line

  const isPhoneDuplicate = () => {
    let isDuplicate = false;
    const valueArr = phones.map((item) => item.phone);
    isDuplicate = valueArr.some((item, idx) => valueArr.indexOf(item) !== idx);
    return isDuplicate;
  };

  const isAccountManagerEmpty = () => {
    let isEmpty = false;
    accountManagers.forEach((accountManager) => {
      if (!accountManager.userId) isEmpty = true;
    });
    return isEmpty;
  };

  const isAccountManagerDuplicate = () => {
    let isDuplicate = false;
    const valueArr = accountManagers.map((item) => item.userId);
    isDuplicate = valueArr.some((item, idx) => valueArr.indexOf(item) !== idx);
    return isDuplicate;
  };

  const isAccountingContactEmpty = () => {
    let isEmpty = false;
    accountingContacts.forEach((accountingContact) => {
      if (!accountingContact.userId) isEmpty = true;
    });
    return isEmpty;
  };

  const isAccountingContactDuplicate = () => {
    let isDuplicate = false;
    const valueArr = accountingContacts.map((item) => item.userId);
    isDuplicate = valueArr.some((item, idx) => valueArr.indexOf(item) !== idx);
    return isDuplicate;
  };

  const handleSelectedPhoneChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    selectedIndex: number
  ) => {
    setSelectedPhone(event.target.value);
    const allPhones = JSON.parse(JSON.stringify(phones));
    if (selectedIndex >= 0) {
      allPhones.forEach((allPhone: any, index: number) => {
        if (index === selectedIndex) {
          allPhone.isPrimary = true;
        } else {
          allPhone.isPrimary = false;
        }
      });
    }
    setPhones(allPhones);
  };

  const altPhoneChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const allPhones = JSON.parse(JSON.stringify(phones));
    allPhones[index].phone = phoneFormatInput(e.target.value);
    setPhones(allPhones);
  };

  const addAltPhoneClick = () => {
    setPhones((prev) => [...prev, { phone: "", isPrimary: false }]);
  };

  const deleteAltPhoneClick = (index: number) => {
    const allPhones = [...phones];
    allPhones.splice(index, 1);
    setPhones(allPhones);
    const selectedPhoneIndex = Number(
      selectedPhone.substring(selectedPhone.length - 1)
    );
    if (selectedPhoneIndex > index)
      setSelectedPhone(`altPhone${selectedPhoneIndex - 1}`);
  };

  const handleSelectedManagerChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    selectedIndex: number
  ) => {
    setSelectedManager(event.target.value);
    const allAccountManagers = JSON.parse(JSON.stringify(accountManagers));
    if (selectedIndex >= 0) {
      allAccountManagers.forEach((accountManager: any, index: number) => {
        if (index === selectedIndex) {
          accountManager.isPrimary = true;
        } else {
          accountManager.isPrimary = false;
        }
      });
    }
    setAccountManagers(allAccountManagers);
  };

  const onAccountManagerChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const allAccountManagers = JSON.parse(JSON.stringify(accountManagers));
    allAccountManagers[index].userId = e.target.value;
    setAccountManagers(allAccountManagers);
  };

  const addAccountManagerClick = () => {
    setAccountManagers((prev) => [...prev, { userId: "", isPrimary: false }]);
  };

  const handleAccountManagerDelete = (index: number) => {
    const allAccountManagers = [...accountManagers];
    allAccountManagers.splice(index, 1);
    setAccountManagers(allAccountManagers);
    const selectedIndex = Number(
      selectedManager.substring(selectedManager.length - 1)
    );
    if (selectedIndex > index)
      setSelectedManager(`contact${selectedIndex - 1}`);
  };

  const handleSelectedContactChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    selectedIndex: number
  ) => {
    setSelectedContact(event.target.value);
    const allAccountingContacts = JSON.parse(
      JSON.stringify(accountingContacts)
    );
    if (selectedIndex >= 0) {
      allAccountingContacts.forEach((accountingContact: any, index: number) => {
        if (index === selectedIndex) {
          accountingContact.isPrimary = true;
        } else {
          accountingContact.isPrimary = false;
        }
      });
    }
    setAccountingContacts(allAccountingContacts);
  };

  const onAccountingContactChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const allAccountingContacts = JSON.parse(
      JSON.stringify(accountingContacts)
    );
    allAccountingContacts[index].userId = e.target.value;
    setAccountingContacts(allAccountingContacts);
  };

  const addAccountingContactClick = () => {
    setAccountingContacts((prev) => [
      ...prev,
      { userId: "", isPrimary: false },
    ]);
  };

  const handleAccountingContactDelete = (index: number) => {
    const allAccountingContacts = [...accountingContacts];
    allAccountingContacts.splice(index, 1);
    setAccountingContacts(allAccountingContacts);
    const selectedIndex = Number(
      selectedContact.substring(selectedContact.length - 1)
    );
    if (selectedIndex > index)
      setSelectedContact(`contact${selectedIndex - 1}`);
  };

  const updateInspectionFirmContacts = async (payload: any) => {
    setShowLoading(true);
    const response = await updateInspectionFirmContactsRequest(payload);
    setShowLoading(false);
    if (response?.status === 204) {
      dispatch(getInspectionFirmDetailsRequest({ id: data?.id }));
      hideDialog();
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const hideDialog = () => {
    formik.resetForm();
    closeDialog();
  };

  return (
    <DialogContainer
      title="Inspection Firm - Contact Details"
      showDialog={showDialog}
      closeDialog={hideDialog}
      onSubmit={formik.handleSubmit}
    >
      <form onSubmit={formik.handleSubmit}>
        <Box className="user-name-layout">
          <Typography className="label-txt">Phone</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <Typography>Primary</Typography>
            {phones.map((item, index) => (
              <AlternativePhoneField
                name="altPhones"
                index={index}
                key={index}
                selectedValue={selectedPhone}
                phones={phones}
                onRadioChange={(e: any) => handleSelectedPhoneChange(e, index)}
                altPhone={item?.phone}
                onPhoneChange={(e: any) => altPhoneChange(e, index)}
                onDelete={() => deleteAltPhoneClick(index)}
                isPhoneEmptyError={!!phoneFieldErrors[index]}
                phoneErrorText={phoneFieldErrors[index]}
              />
            ))}
            {phones?.length < 4 && (
              <Button
                sx={{ mt: 2 }}
                variant="text"
                startIcon={<AddOutlinedIcon />}
                onClick={addAltPhoneClick}
              >
                Add Alt Phone
              </Button>
            )}
          </Box>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box className="user-name-layout">
          <Typography className="label-txt">Mailing Address</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <FormControl
              sx={{ width: "100% !important" }}
              error={
                formik.touched.mailingCountry &&
                Boolean(formik.errors.mailingCountry)
              }
            >
              <InputLabel id="country-select-label">Country</InputLabel>
              <Select
                labelId="country-select-label"
                id="mailingCountry"
                name="mailingCountry"
                label="Country"
                value={formik.values.mailingCountry}
                onChange={formik.handleChange}
              >
                {countries?.map((country: string) => (
                  <MenuItem value={country}>{country}</MenuItem>
                ))}
              </Select>
              {Boolean(formik.errors.mailingCountry) &&
                formik.touched.mailingCountry && (
                  <Typography
                    sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                    color="error"
                  >
                    This field can't be empty
                  </Typography>
                )}
            </FormControl>
            <TextInput
              sx={{ width: "100% !important", mt: 2 }}
              className="field-input"
              id="mailingStreetAddress"
              name="mailingStreetAddress"
              label="Street Address"
              variant="outlined"
              value={formik.values.mailingStreetAddress}
              onChange={formik.handleChange}
              error={
                formik.touched.mailingStreetAddress &&
                Boolean(formik.errors.mailingStreetAddress)
              }
              helperText={
                formik.touched.mailingStreetAddress &&
                formik.errors.mailingStreetAddress
              }
            />
            <TextInput
              sx={{ mt: 2, width: "100% !important" }}
              className="field-input"
              id="mailingAddress2"
              name="mailingAddress2"
              label="Address 2 (Optional)"
              variant="outlined"
              value={formik.values.mailingAddress2}
              onChange={formik.handleChange}
              error={
                formik.touched.mailingAddress2 &&
                Boolean(formik.errors.mailingAddress2)
              }
              helperText={
                formik.touched.mailingAddress2 && formik.errors.mailingAddress2
              }
            />
            <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
              <TextField
                sx={{ flex: 1 }}
                id="mailingCity"
                name="mailingCity"
                label="City"
                variant="outlined"
                value={formik.values.mailingCity}
                onChange={formik.handleChange}
                error={
                  formik.touched.mailingCity &&
                  Boolean(formik.errors.mailingCity)
                }
                helperText={
                  formik.touched.mailingCity && formik.errors.mailingCity
                }
              />
              <FormControl
                sx={{ flex: 1, ml: 2 }}
                error={
                  formik.touched.mailingState &&
                  Boolean(formik.errors.mailingState)
                }
              >
                <InputLabel id="state-select-label">State</InputLabel>
                <Select
                  labelId="state-select-label"
                  id="mailingState"
                  name="mailingState"
                  label="State"
                  value={formik.values.mailingState}
                  onChange={formik.handleChange}
                >
                  {mailingStates?.map((state: string) => (
                    <MenuItem value={state}>{state}</MenuItem>
                  ))}
                </Select>
                {formik.errors.mailingState && formik.touched.mailingState && (
                  <Typography
                    sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                    color="error"
                  >
                    This field can't be empty
                  </Typography>
                )}
              </FormControl>
              <TextField
                sx={{ flex: 1, ml: 2 }}
                id="mailingZip"
                name="mailingZip"
                label="Zip"
                variant="outlined"
                value={formik.values.mailingZip}
                onChange={formik.handleChange}
                error={
                  formik.touched.mailingZip && Boolean(formik.errors.mailingZip)
                }
                helperText={
                  formik.touched.mailingZip && formik.errors.mailingZip
                }
              />
            </Box>
          </Box>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box className="user-name-layout">
          <Typography className="label-txt">Billing Address</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <FormControl
              sx={{ width: "100% !important" }}
              error={
                formik.touched.billingCountry &&
                Boolean(formik.errors.billingCountry)
              }
            >
              <InputLabel id="country-select-label">Country</InputLabel>
              <Select
                labelId="country-select-label"
                id="billingCountry"
                name="billingCountry"
                label="Country"
                value={formik.values.billingCountry}
                onChange={formik.handleChange}
              >
                {countries?.map((country: string) => (
                  <MenuItem value={country}>{country}</MenuItem>
                ))}
              </Select>
              {formik.errors.billingCountry &&
                formik.touched.billingCountry && (
                  <Typography
                    sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                    color="error"
                  >
                    This field can't be empty
                  </Typography>
                )}
            </FormControl>
            <TextInput
              sx={{ width: "100% !important", mt: 2 }}
              className="field-input"
              id="billingStreetAddress"
              name="billingStreetAddress"
              label="Street Address"
              variant="outlined"
              value={formik.values.billingStreetAddress}
              onChange={formik.handleChange}
              error={
                formik.touched.billingStreetAddress &&
                Boolean(formik.errors.billingStreetAddress)
              }
              helperText={
                formik.touched.billingStreetAddress &&
                formik.errors.billingStreetAddress
              }
            />
            <TextInput
              sx={{ mt: 2, width: "100% !important" }}
              className="field-input"
              id="billingAddress2"
              name="billingAddress2"
              label="Address 2 (Optional)"
              variant="outlined"
              value={formik.values.billingAddress2}
              onChange={formik.handleChange}
              error={
                formik.touched.billingAddress2 &&
                Boolean(formik.errors.billingAddress2)
              }
              helperText={
                formik.touched.billingAddress2 && formik.errors.billingAddress2
              }
            />
            <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
              <TextField
                sx={{ flex: 1 }}
                id="billingCity"
                name="billingCity"
                label="City"
                variant="outlined"
                value={formik.values.billingCity}
                onChange={formik.handleChange}
                error={
                  formik.touched.billingCity &&
                  Boolean(formik.errors.billingCity)
                }
                helperText={
                  formik.touched.billingCity && formik.errors.billingCity
                }
              />
              <FormControl
                sx={{ flex: 1, ml: 2 }}
                error={
                  formik.touched.billingState &&
                  Boolean(formik.errors.billingState)
                }
              >
                <InputLabel id="state-select-label">State</InputLabel>
                <Select
                  labelId="state-select-label"
                  id="billingState"
                  name="billingState"
                  label="State"
                  value={formik.values.billingState}
                  onChange={formik.handleChange}
                >
                  {billingStates?.map((state: string) => (
                    <MenuItem value={state}>{state}</MenuItem>
                  ))}
                </Select>
                {formik.errors.billingState && formik.touched.billingState && (
                  <Typography
                    sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                    color="error"
                  >
                    This field can't be empty
                  </Typography>
                )}
              </FormControl>
              <TextField
                sx={{ flex: 1, ml: 2 }}
                id="billingZip"
                name="billingZip"
                label="Zip"
                variant="outlined"
                value={formik.values.billingZip}
                onChange={formik.handleChange}
                error={
                  formik.touched.billingZip && Boolean(formik.errors.billingZip)
                }
                helperText={
                  formik.touched.billingZip && formik.errors.billingZip
                }
              />
            </Box>
          </Box>
        </Box>
        <Divider sx={{ mt: 1 }} />
        <Box className="user-name-layout">
          <Typography className="label-txt">Account Manager/Admin</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <Typography>Primary</Typography>
            {accountManagers.map((item, index) => (
              <AdditionalContactField
                index={index}
                key={index}
                selectedValue={selectedManager}
                onRadioChange={(e: any) =>
                  handleSelectedManagerChange(e, index)
                }
                value={item.userId}
                onValueChange={(e: any) => onAccountManagerChange(e, index)}
                onDelete={() => handleAccountManagerDelete(index)}
                allUsers={inspectionFirmAllUsers}
                selectedUsers={accountManagers}
                isUserEmptyError={isAccountManagerEmptyError}
              />
            ))}
            {accountManagers?.length < 4 && (
              <Button
                sx={{ mt: 2, fontWeight: "510" }}
                variant="text"
                startIcon={<AddOutlinedIcon />}
                onClick={addAccountManagerClick}
              >
                Add Additional
              </Button>
            )}
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Accounting Contact</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <Typography>Primary</Typography>
            {accountingContacts.map((item, index) => (
              <AdditionalContactField
                index={index}
                key={index}
                selectedValue={selectedContact}
                onRadioChange={(e: any) =>
                  handleSelectedContactChange(e, index)
                }
                value={item.userId}
                onValueChange={(e: any) => onAccountingContactChange(e, index)}
                onDelete={() => handleAccountingContactDelete(index)}
                allUsers={inspectionFirmAllUsers}
                selectedUsers={accountingContacts}
                isUserEmptyError={isAccountingContactEmptyError}
              />
            ))}
            {accountingContacts?.length < 4 && (
              <Button
                sx={{ mt: 2, fontWeight: "510" }}
                variant="text"
                startIcon={<AddOutlinedIcon />}
                onClick={addAccountingContactClick}
              >
                Add Additional
              </Button>
            )}
          </Box>
        </Box>
      </form>
      {(loading || showLoading) && <Loading />}
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          message={errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
      )}
    </DialogContainer>
  );
}
